import { IComponentOptions, IAugmentedJQuery, IOnChangesObjectOf, ITimeoutService } from "angular";
import { GtmhubController } from "@gtmhub/core";
import { handleOutsideMouseEvent, handleInsideMouseEvent } from "@gtmhub/shared/directives/close-on-mousedown";
import { IGridSortingRule, IGridSortingDefinition, GridSortingDefinitions } from "./models";
import { localize } from "@gtmhub/localization";
import { GridFilteringBarComponentCtrl } from "../grid-filtering-bar.component";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import debounce from "lodash.debounce";

export interface IGridSortingComponentRequire {
  gridFilteringBar: GridFilteringBarComponentCtrl;
}

export interface IGridSortingComponentBindings {
  definitions: GridSortingDefinitions;
  rules: IGridSortingRule[];
  maxRules: number;
  rulesChange(): void;
  closeOnClick: string;
  debounce?: number;
}

type SortingOrder = "ascending" | "descending";

/**
 * Provides sorting functionality for grid layouts (e.g., goals, metrics, employees). It must be
 * used within `grid-filtering-bar` as its parent.
 *
 * `definitions` contains sorting definitions, which contain what one can sort on.
 *
 * `rules` are the applied sorting rules.
 *
 * `rules-change` is fired when sorting rules are changed.
 *
 * `close-on-click` specifies in which selectors when clicked to close the popover.
 *
 * `debounce` specifies the amount of time in miliseconds to wait before firing the next filtersChange event.
 *
 * @example
 * <grid-filtering-bar>
 *   <grid-sorting
 *     definitions="$ctrl.sortingDefinitions"
 *     rules="$ctrl.rules"
 *     rules-change="$ctrl.sortingChange()"
 *     debounce="500">
 *   </grid-sorting>
 * </grid-filtering-bar>
 */
export interface GridSortingComponentCtrl extends IGridSortingComponentBindings, IGridSortingComponentRequire {}
export class GridSortingComponentCtrl extends GtmhubController {
  isOpen: boolean;
  usedDefinitions: Set<string>;
  sortingOrder: Record<SortingOrder, string>;
  debouncedRulesChange: () => void;

  static $inject = ["$element", "$timeout", "$rootScope"];

  constructor(private $element: IAugmentedJQuery, private $timeout: ITimeoutService, private $rootScope: ITraceRootScopeService) {
    super();
  }

  get unusedDefinitions(): IGridSortingDefinition[] {
    return Object.values(this.definitions).filter((d) => !this.usedDefinitions.has(d.fieldName));
  }

  $onInit(): void {
    this.onDestroy(handleOutsideMouseEvent("mousedown", () => this.$timeout(() => (this.isOpen = false)), this.$element));

    if (this.closeOnClick) {
      this.onDestroy(handleInsideMouseEvent("click", () => this.$timeout(() => (this.isOpen = false)), ...this.closeOnClick.split(",")));
    }

    this.sortingOrder = {
      ascending: localize("ascending"),
      descending: localize("descending"),
    };
  }

  $onChanges(changes: IOnChangesObjectOf<IGridSortingComponentBindings>): void {
    if (changes.rules) {
      const rules = changes.rules.currentValue || [];
      this.usedDefinitions = new Set<string>(rules.map((rule) => rule.fieldName));
    }

    if (changes.debounce) {
      if (changes.debounce.currentValue) {
        this.debouncedRulesChange = debounce(() => {
          this.rulesChange();
        }, changes.debounce.currentValue);
      } else {
        this.debouncedRulesChange = null;
      }
    }
  }

  changeFieldName(index: number, fieldName: string): void {
    this.$rootScope.traceAction("sorting_field_updated", () => {
      if (!(fieldName in this.definitions)) {
        throw new Error(`Unknown field name: ${fieldName}`);
      }
      this.$rootScope;

      const rule = this.ruleAt(index);

      this.usedDefinitions.delete(rule.fieldName);
      this.usedDefinitions.add(fieldName);

      const isFieldName = fieldName;
      if (rule.fieldName !== isFieldName) {
        rule.fieldName = fieldName;
        this.callRulesChangeOrDebounce();

        const definition = this.definitions[fieldName];
        this.gridFilteringBar.track("Sorting Field Updated", { field_name: definition.trackingName || definition.displayName });
      }
    });
  }

  changeOrder(index: number, order: SortingOrder): void {
    this.$rootScope.traceAction("sorting_order_changed", () => {
      const rule = this.ruleAt(index);

      const isReversedOrder = order === "descending";
      if (rule.reversed !== isReversedOrder) {
        rule.reversed = order === "descending";
        this.callRulesChangeOrDebounce();

        const definition = this.definitions[rule.fieldName];
        this.gridFilteringBar.track("Sorting Order Changed", {
          field_name: definition.trackingName || definition.displayName,
          order,
        });
      }
    });
  }

  createRule(fieldName: string): void {
    if (!(fieldName in this.definitions)) {
      throw new Error(`Cannot find sorting definition for ${fieldName}`);
    }

    this.$rootScope.traceAction("sorting_rule_added", () => {
      const rule: IGridSortingRule = {
        fieldName: fieldName,
        reversed: true,
      };
      this.rules.push(rule);
      this.usedDefinitions.add(rule.fieldName);
      this.callRulesChangeOrDebounce();

      const definition = this.definitions[rule.fieldName];
      this.gridFilteringBar.track("Sorting Rule Added", { field_name: definition.trackingName || definition.displayName });
    });
  }

  removeRule(index: number): void {
    this.$rootScope.traceAction("sorting_rule_removed", () => {
      const rule = this.ruleAt(index);
      this.usedDefinitions.delete(rule.fieldName);
      this.rules.splice(index, 1);
      this.callRulesChangeOrDebounce();

      const definition = this.definitions[rule.fieldName];
      this.gridFilteringBar.track("Sorting Rule Removed", { field_name: definition.trackingName || definition.displayName });
    });
  }

  notifySortingChange(): void {
    this.$rootScope.traceAction("sorting_rule_changed", () => {
      this.callRulesChangeOrDebounce();
    });
  }

  shouldDisplayMoreOptions(): boolean {
    const isSizeNotExceeded = this.usedDefinitions.size < Object.keys(this.definitions).length;

    return !this.maxRules ? isSizeNotExceeded : this.usedDefinitions.size < this.maxRules && isSizeNotExceeded;
  }

  private callRulesChangeOrDebounce(): void {
    if (this.debouncedRulesChange) {
      this.debouncedRulesChange();
    } else {
      this.rulesChange();
    }
  }
  private ruleAt(index: number): IGridSortingRule {
    if (index < 0 || index >= this.rules.length) {
      throw new Error(`No rule at index ${index}`);
    }

    return this.rules[index];
  }
}

export const GridSortingComponent: IComponentOptions = {
  templateUrl: require("./grid-sorting.jade"),
  controller: GridSortingComponentCtrl,
  require: { gridFilteringBar: "^" },
  bindings: {
    closeOnClick: "@",
    definitions: "<",
    rules: "<",
    maxRules: "<?",
    rulesChange: "&",
    debounce: "<",
  },
};
